import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';

import {
  CloseButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SubmitButton,
  ZoneCreateStatusDots,
} from 'components';
import { ZoneForm } from 'forms';
import { DecalApproveDialog } from 'dialogs/decal';
import { IZone } from 'models';
import { EZoneCreateStatus } from 'enums';
import { ROUTES } from 'consts';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices';

interface ZoneCreateDialogProps {
  visible?: boolean;
  setVisible?: (value: boolean) => void;
  title?: string;
  submitButtonLabel?: string;
  buildingId?: number | null;
}

export const ZoneCreateDialog = ({
  visible = false,
  setVisible = () => {},
  title = 'Add new zone',
  submitButtonLabel = 'Approve Decal Next',
  buildingId = null,
}: ZoneCreateDialogProps) => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [loading, setLoading] = useState<boolean>(false);
  const [decalApproveDialog, setDecalApproveDialog] = useState<{ visible: boolean; zone?: IZone }>({ visible: false });
  const navigate = useNavigate();
  const formRef = useRef<any>();
  const theme = useTheme();
  const down_sm = useMediaQuery(theme.breakpoints.down('sm'));

  const onClose = (event: any, reason: string) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
      setVisible(false);
      document.body.removeAttribute('style');
    }
  };

  const onSubmit = () => {
    formRef.current.onSubmit();
  };

  return (
    <>
      <Dialog
        onClose={onClose}
        open={visible}
        PaperProps={{ sx: { marginBottom: 12, ...(down_sm ? {} : { overflow: 'visible' }) } }}
      >
        <DialogTitle>
          <Typography variant="h5" component="span">
            {title}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <ZoneForm
            ref={formRef}
            initialBuildingId={buildingId}
            onSubmitting={(loading) => setLoading(loading)}
            next={(zone) => {
              setVisible(false);
              setDecalApproveDialog({ visible: true, zone });
            }}
          />
        </DialogContent>

        <DialogActions>
          <SubmitButton loading={loading} label={submitButtonLabel} onClick={onSubmit} />
        </DialogActions>

        <CloseButton onClick={() => onClose(null, 'anyway')} />

        <Box
          sx={{
            width: '100%',
            left: 0,
            display: 'flex',
            justifyContent: 'center',
            ...(down_sm ? { paddingBottom: 6 } : { position: 'absolute', bottom: -60 }),
          }}
        >
          <ZoneCreateStatusDots status={EZoneCreateStatus.ADD_ZONE} />
        </Box>
      </Dialog>

      <DecalApproveDialog
        {...decalApproveDialog}
        setVisible={() => setDecalApproveDialog({ visible: false })}
        next={() => {
          let url = `/${orgSlug}/${ROUTES.ZONES}/${ROUTES.ZONES_ACTIVE}`;

          if (buildingId) {
            url += `?building=${buildingId}`;
          }

          navigate(url);
        }}
      />
    </>
  );
};
